import axios from 'axios';

/**
 * Task 27G - Dynamic Endpoint Resolution with Runtime Validation
 * Automatically detects the correct API base URL based on browser location
 * Priority: Auto-detection FIRST, override only when explicitly needed
 */
const getAPIBaseURL = () => {
  const protocol = window.location.protocol; // http: or https:
  const host = window.location.hostname;     // localhost, 192.168.x.x, 10.x.x.x, domain
  
  // Determine environment type
  let envType = 'Unknown';
  if (host === 'localhost' || host === '127.0.0.1') {
    envType = 'Local';
  } else if (host.startsWith('192.168.') || host.startsWith('10.')) {
    envType = 'LAN';
  } else {
    envType = 'Cloud/Production';
  }

  // Check for manual override
  const override = import.meta.env.VITE_API_BASE_URL;
  if (override) {
    console.warn(`⚠️  SPES: Using VITE_API_BASE_URL override → ${override}`);
    console.warn(`⚠️  SPES: Auto-detection DISABLED. If backend IP changed, clear .env and restart!`);
    console.log(`🌐 SPES: Resolved environment = ${envType} (OVERRIDE)`);
    return override;
  }

  // Auto-detect from browser location
  let base = `${protocol}//${host}:8000`;

  // Production domain uses relative /api path (no port 8000)
  if (protocol === 'https:' && (host === 'app.spes.in' || host === 'www.app.spes.in')) {
    base = '/api';
    console.log(`🌐 SPES: Production domain detected → ${base}`);
  } else if (protocol === 'https:' && host !== 'localhost') {
    console.warn(`⚠️  SPES: HTTPS detected. If backend is behind proxy, set VITE_API_BASE_URL to /api`);
  }

  console.log(`🌐 SPES: Auto-detected backend → ${base}`);
  console.log(`🌐 SPES: Resolved environment = ${envType}`);
  console.log(`✅ SPES: Dynamic detection ACTIVE (no .env override)`);
  
  return base;
};

// Export the resolved API base URL
export const API_BASE_URL = getAPIBaseURL();

// Create global Axios instance with dynamic base URL
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Request interceptor: Add Authorization header with JWT token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('spes_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors (redirect to login), 403 errors (show message)
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log('[axios-interceptor] Response error caught:', error);
    
    if (error.response) {
      const { status } = error.response;
      console.log('[axios-interceptor] Error status:', status);
      console.log('[axios-interceptor] Current path:', window.location.pathname);
      
      // Task 27H: 401 = Unauthorized (invalid/expired token) → Logout
      if (status === 401) {
        console.error('[axios-interceptor] ❌ 401 Unauthorized - clearing session and redirecting');
        console.error('[axios-interceptor] Request was:', error.config?.url);
        
        localStorage.removeItem('spes_token');
        localStorage.removeItem('spes_role');
        localStorage.removeItem('spes_user');
        
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          console.error('[axios-interceptor] Redirecting to /login');
          window.location.href = '/login';
        }
      }
      
      // Task 27H: 403 = Forbidden (authenticated but no permission) → Don't logout, let component handle
      if (status === 403) {
        console.warn('[axios-interceptor] ⚠️  403 Forbidden - You don\'t have permission for this resource');
        console.warn('[axios-interceptor] Request was:', error.config?.url);
        console.warn('[axios-interceptor] User remains logged in - component should show error message');
        // Don't clear session - just let the error propagate to component
      }
    } else {
      console.log('[axios-interceptor] No response object - network error?');
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
